import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  styled,
  Typography
} from '@mui/material';
import {
  activityTemplates,
  AuthWrapper,
  imageUtil,
  routeDirectory
} from 'modules';

import { useNavigate } from 'react-router-dom';
import * as dataService from './dataService';

import imgBackground from 'assets/images/home_background.png';



const styleConstants = {
  imageHeight: 205,
  chipHeight: 50
};



const StyledTypography = styled(Typography)(({ theme }) => ({
  color: '#C3D1D8',
  marginBottom: theme.spacing(2)
}));

const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  position: 'relative'
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: styleConstants.chipHeight,
  position: 'absolute',
  right: 14,
  top: styleConstants.imageHeight - (styleConstants.chipHeight / 2),
  borderRadius: styleConstants.chipHeight / 2,
  border: '5px solid #FFFFFF',
  color: theme.palette.primary.main,
  backgroundColor: '#FFDFD8',
  fontSize: 14,
  fontFamily: 'Noto Serif'
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: 0
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  lineHeight: 'normal',
  letterSpacing: 'normal'
}));

const DescriptionTypography = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  letterSpacing: 0.25,
  lineHeight: 1.57,
  marginBottom: theme.spacing(1)
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingBottom: theme.spacing(3)
}));

const RowBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: theme.spacing(2)
}));

export const HomeActivity: React.FC = props => {
  const navigate = useNavigate();
  const { loading, data } = dataService.useHomeActivity();



  React.useEffect(() => {
    if (!loading && !data.email) {
      navigate(routeDirectory.authentication.login.path());
    }
  }, [loading, data, navigate]);



  return (
    <AuthWrapper.FC>
      <activityTemplates.BaseActivityTemplate.FC
        imgBackground={imgBackground}
        colorVariant="dark">
        <StyledTypography variant="h1">
          Welcome, {data.firstName}
        </StyledTypography>

        <RowBox>
          {_.map(__cards, (card, i) => (
            <StyledCard key={i}>
              <CardMedia
                component="img"
                height={styleConstants.imageHeight}
                image={card.imgUrl} />

              {i === 0 && (
                <StyledChip label="Start Here" />
              )}

              <StyledCardContent>
                <CardTitle variant="h2">
                  {card.title}
                </CardTitle>

                <DescriptionTypography variant="body1">
                  {card.description}
                </DescriptionTypography>
              </StyledCardContent>

              <StyledCardActions>
                <Button
                  variant="outlined"
                  onClick={() => navigate(card.path)}>View Now</Button>
              </StyledCardActions>
            </StyledCard>
          ))}
        </RowBox>
      </activityTemplates.BaseActivityTemplate.FC>
    </AuthWrapper.FC>
  );
};



interface ICard {
  path: string;
  title: string;
  description: string;
  imgUrl: string;
}

const __cards: ICard[] = [
  {
    title: 'Pitch Deck Development',
    description: 'Explore and download designed pitch deck slides to kickstart your fundraise.',
    imgUrl: _.last(imageUtil.getUrlSet('pitch_decks_card', 'png')),
    path: routeDirectory.pitchDecks.path()
  },
  {
    title: 'Investor Search',
    description: 'Search, filter, and favorite investors for your fundraise process.',
    imgUrl: _.last(imageUtil.getUrlSet('investors_card', 'png')),
    path: routeDirectory.investors.path()
  },
  {
    title: 'My Fundraises',
    description: 'Create and track your fundraises here based on the investors you favorite.',
    imgUrl: _.last(imageUtil.getUrlSet('my_fundraises_card', 'png')),
    path: routeDirectory.myFundraises.path()
  },
  {
    title: 'Legal',
    description: 'Review term sheet details and implications for your cap table.',
    imgUrl: _.last(imageUtil.getUrlSet('legal', 'png')),
    path: routeDirectory.legal.path()
  }
];
